import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Play, Pause, SkipBack, SkipForward, Volume2, VolumeX, X, Music, ListMusic } from 'lucide-react';
import { usePlayer, fmtTime } from '../context/PlayerContext';
import { resolveFileUrl } from '../lib/api';

/**
 * Persistent mini-player bar mounted in AppLayout. Fixed bottom, visible only
 * when a track is loaded. Auto-hides on /feed (the reel has its own UI).
 */
export default function MiniPlayer() {
  const loc = useLocation();
  const { current, playing, toggle, next, prev, seek, progress, currentTime, duration, volume, setVolume, stop, queue } = usePlayer();
  const [showQueue, setShowQueue] = useState(false);

  if (!current) return null;
  if (loc.pathname === '/feed') return null; // reel takes over

  const cover = resolveFileUrl(current.cover_url);

  return (
    <>
      <div
        className="fixed left-0 right-0 bottom-0 z-[55] theme-navbar border-t border-white/10"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0)' }}
        data-testid="mini-player"
      >
        {/* Progress bar — clickable to seek */}
        <div className="h-1 bg-white/5 cursor-pointer group"
             onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); seek((e.clientX - r.left) / r.width); }}>
          <div className="h-full transition-[width] duration-200" style={{ width: `${progress * 100}%`, background: 'linear-gradient(90deg,#a78bfa,#22d3ee)' }} />
        </div>

        <div className="flex items-center gap-3 px-3 md:px-5 py-2">
          {/* Track meta */}
          <Link to={current.channel_id ? `/channel/${current.channel_id}` : '/feed'} className="flex items-center gap-3 min-w-0 flex-1">
            <div className="w-10 h-10 md:w-12 md:h-12 rounded-md overflow-hidden shrink-0 bg-white/5 grid place-items-center">
              {cover ? <img src={cover} alt="" className="w-full h-full object-cover" /> : <Music className="w-4 h-4 text-white/40" />}
            </div>
            <div className="min-w-0 hidden sm:block">
              <div className="text-[13px] font-semibold truncate" data-testid="miniplayer-title">{current.title}</div>
              <div className="text-[11px] text-white/55 truncate">{current.artist_name || '—'}</div>
            </div>
          </Link>

          {/* Transport */}
          <div className="flex items-center gap-1 md:gap-2 shrink-0">
            <button onClick={prev} aria-label="Previous" className="w-8 h-8 md:w-9 md:h-9 rounded-full grid place-items-center hover:bg-white/10" data-testid="miniplayer-prev"><SkipBack className="w-4 h-4" /></button>
            <button onClick={toggle} aria-label={playing ? 'Pause' : 'Play'}
                    className="w-10 h-10 rounded-full grid place-items-center text-black"
                    style={{ background: 'linear-gradient(135deg,#a78bfa,#22d3ee)' }}
                    data-testid="miniplayer-toggle">
              {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            </button>
            <button onClick={next} aria-label="Next" className="w-8 h-8 md:w-9 md:h-9 rounded-full grid place-items-center hover:bg-white/10" data-testid="miniplayer-next"><SkipForward className="w-4 h-4" /></button>
          </div>

          {/* Time + volume + queue (desktop only) */}
          <div className="hidden lg:flex items-center gap-3 text-[11px] text-white/65 tabular-nums">
            <span data-testid="miniplayer-time">{fmtTime(currentTime)} / {fmtTime(duration)}</span>
            <VolumeControl volume={volume} onChange={setVolume} />
            <button onClick={() => setShowQueue(v => !v)} className="w-8 h-8 rounded-full grid place-items-center hover:bg-white/10 relative" aria-label="Queue" data-testid="miniplayer-queue">
              <ListMusic className="w-4 h-4" />
              {queue.length > 0 && <span className="absolute -top-0.5 -right-0.5 bg-[#ff2e63] text-white text-[9px] font-bold rounded-full px-1 min-w-[14px] h-[14px] grid place-items-center">{queue.length}</span>}
            </button>
          </div>

          {/* Close */}
          <button onClick={stop} aria-label="Close player" className="w-8 h-8 rounded-full grid place-items-center hover:bg-white/10 text-white/65 hover:text-white" data-testid="miniplayer-close">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Queue popover */}
      {showQueue && (
        <div className="fixed right-4 bottom-20 z-[56] card-shell p-2 w-[320px] max-h-[360px] overflow-y-auto" data-testid="miniplayer-queue-panel">
          <div className="px-2 pt-1 pb-2 text-[11px] uppercase tracking-wider text-white/55 font-semibold">Up next · {queue.length}</div>
          {queue.length === 0 ? (
            <div className="p-3 text-[13px] text-white/55">Queue is empty.</div>
          ) : queue.map((t, i) => (
            <div key={`${t.id}-${i}`} className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-white/5">
              <div className="w-8 h-8 rounded bg-white/5 overflow-hidden shrink-0">{t.cover_url && <img src={resolveFileUrl(t.cover_url)} alt="" className="w-full h-full object-cover" />}</div>
              <div className="min-w-0 flex-1">
                <div className="text-[12.5px] truncate">{t.title}</div>
                <div className="text-[11px] text-white/50 truncate">{t.artist_name}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function VolumeControl({ volume, onChange }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative" onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>
      <button onClick={() => onChange(volume > 0 ? 0 : 0.9)} className="w-8 h-8 rounded-full grid place-items-center hover:bg-white/10" aria-label="Volume">
        {volume > 0 ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
      </button>
      {show && (
        <div className="absolute right-0 bottom-9 card-shell p-2 w-36">
          <input type="range" min={0} max={100} value={Math.round(volume * 100)} onChange={e => onChange(parseInt(e.target.value) / 100)} className="w-full accent-[#ff2e63]" />
        </div>
      )}
    </div>
  );
}
